// src/components/common/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WEBSITE_NAME } from '../../utils/constants';
import './Header.css';

const Header = () => {
  const [isGetStartedOpen, setIsGetStartedOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isBusinessTypeHovered, setIsBusinessTypeHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobile) return;
      
      if (!e.target.closest('.nav-dropdown-wrapper')) {
        setIsGetStartedOpen(false);
        setIsSolutionsOpen(false);
        setIsResourcesOpen(false);
        setIsAboutOpen(false);
        setIsBusinessTypeHovered(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen, isMobile]);

  return (
    <header className="header-main">
      <div className="header-container">
        {/* Logo Section - ALWAYS VISIBLE */}
        <div className="header-left">
          <Link to="/" className="logo">
            <span className="logo-icon">V</span>
            <span className="logo-text">{WEBSITE_NAME}</span>
          </Link>

          {/* DESKTOP NAVIGATION - ORIGINAL LIKE BEFORE */}
          {!isMobile && (
            <nav className="navbar">
              {/* Get Started Dropdown */}
              <div 
                className="nav-dropdown-wrapper" 
                onMouseEnter={() => setIsGetStartedOpen(true)}
                onMouseLeave={() => setIsGetStartedOpen(false)}
              >
                <div className={`nav-dropdown-trigger ${isGetStartedOpen ? 'active' : ''}`}>
                  Get Started
                  <svg className={`nav-arrow ${isGetStartedOpen ? 'rotated' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {isGetStartedOpen && (
                  <div className="nav-dropdown-menu">
                    <div className="dropdown-nested-parent"
                         onMouseEnter={() => setIsBusinessTypeHovered(true)}
                         onMouseLeave={() => setIsBusinessTypeHovered(false)}>
                      <div className={`dropdown-nested-trigger ${isBusinessTypeHovered ? 'active' : ''}`}>
                        Choose a Business Type
                        <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      
                      {isBusinessTypeHovered && (
                        <div className="dropdown-nested-menu">
                          <Link to="/beginners" className="nested-link">
                            <p className="nested-title">New Sellers & Beginners</p>
                            <p className="nested-description">Exploring or Just Getting Started</p>
                          </Link>
                          <Link to="/advanced" className="nested-link">
                            <p className="nested-title">Advanced Sellers</p>
                            <p className="nested-description">Actively Selling & Scaling</p>
                          </Link>
                          <Link to="/brands" className="nested-link">
                            <p className="nested-title">Brands</p>
                            <p className="nested-description">Enterprise Businesses</p>
                          </Link>
                          <Link to="/agencies" className="nested-link">
                            <p className="nested-title">Agencies</p>
                            <p className="nested-description">Brand Marketing Firms</p>
                          </Link>
                          <Link to="/influencers" className="nested-link">
                            <p className="nested-title">Social Media Influencers</p>
                            <p className="nested-description">Earn Commission with Product Reviews</p>
                          </Link>
                        </div>
                      )}
                    </div>
                    
                    <Link to="/marketplaces" className="dropdown-link">
                      Choose a Marketplace
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                )}
              </div>

              {/* Solutions Dropdown */}
              <div 
                className="nav-dropdown-wrapper"
                onMouseEnter={() => setIsSolutionsOpen(true)}
                onMouseLeave={() => setIsSolutionsOpen(false)}
              >
                <div className={`nav-dropdown-trigger ${isSolutionsOpen ? 'active' : ''}`}>
                  Solutions
                  <svg className={`nav-arrow ${isSolutionsOpen ? 'rotated' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {isSolutionsOpen && (
                  <div className="nav-dropdown-menu">
                    <Link to="/tools/product-research" className="dropdown-link">
                      Explore Product Research Tools
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                    <Link to="/tools/keyword-research" className="dropdown-link">
                      Explore Keyword Research Tools
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                    <Link to="/tools/listing-optimization" className="dropdown-link">
                      Improve Your Product Listings
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                    <Link to="/tools/operations" className="dropdown-link">
                      Manage Your Backend Operations
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                    <Link to="/tools/analytics" className="dropdown-link">
                      Explore Analytics Tools
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                    <Link to="/tools/ads" className="dropdown-link">
                      Explore Ad Tools
                      <svg className="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                )}
              </div>

              {/* Pricing Link */}
              <Link to="/#pricing" className="nav-link">Pricing</Link>

              {/* Resources Mega Menu */}
              <div 
                className="nav-dropdown-wrapper"
                onMouseEnter={() => setIsResourcesOpen(true)}
                onMouseLeave={() => setIsResourcesOpen(false)}
              >
                <div className={`nav-dropdown-trigger ${isResourcesOpen ? 'active' : ''}`}>
                  Resources
                  <svg className={`nav-arrow ${isResourcesOpen ? 'rotated' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {isResourcesOpen && (
                  <div className="mega-menu">
                    <div className="mega-grid">
                      <div>
                        <h3 className="mega-section-title">LEARNING</h3>
                        <div className="mega-links-section">
                          <Link to="/freedom-ticket" className="mega-link">
                            <p className="mega-title">Freedom Ticket</p>
                            <p className="mega-description">Our award-winning training series</p>
                          </Link>
                          <Link to="/ads-academy" className="mega-link">
                            <p className="mega-title">Amazon Ads Academy</p>
                            <p className="mega-description">Amazon Ads Training for All Levels</p>
                          </Link>
                          <Link to="/podcast" className="mega-link">
                            <p className="mega-title">Podcast</p>
                            <p className="mega-description">Listen on-the-go</p>
                          </Link>
                          <Link to="/webinars" className="mega-link">
                            <p className="mega-title">Webinars</p>
                            <p className="mega-description">Learn the Tactics</p>
                          </Link>
                        </div>
                      </div>
                      <div>
                        <h3 className="mega-section-title">LATEST FROM HELIUM 10</h3>
                        <div className="mega-links-section">
                          <Link to="/exit-ticket" className="mega-link">
                            <p className="mega-title">Exit Ticket</p>
                            <p className="mega-description">E-Commerce Business Exit Strategy</p>
                          </Link>
                          <Link to="/academy" className="mega-link">
                            <p className="mega-title">Academy</p>
                            <p className="mega-description">Master Our Software</p>
                          </Link>
                          <Link to="/scale-stories" className="mega-link">
                            <p className="mega-title">Scale Stories</p>
                            <p className="mega-description">Watch real sellers start and scale their business</p>
                          </Link>
                          <Link to="/customer-spotlight" className="mega-link">
                            <p className="mega-title">Customer Spotlight</p>
                            <p className="mega-description">Proven success for sellers of every size</p>
                          </Link>
                          <Link to="/solutions-hub" className="mega-link">
                            <p className="mega-title">Seller Solutions Hub</p>
                            <p className="mega-description">Export help for your business on Amazon</p>
                          </Link>
                          <Link to="/tariff-center" className="mega-link">
                            <p className="mega-title">Tariff Strategy Center</p>
                            <p className="mega-description">Navigate Tariff Updates</p>
                          </Link>
                          <Link to="/project-x" className="mega-link">
                            <p className="mega-title">Project X</p>
                            <p className="mega-description">Amazon FBA Case Study</p>
                          </Link>
                          <Link to="/project-w" className="mega-link">
                            <p className="mega-title">Project W</p>
                            <p className="mega-description">How to Sell on Walmart.com</p>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Blog Link */}
              <Link to="/blog" className="nav-link">Blog</Link>

              {/* About Dropdown */}
              <div 
                className="nav-dropdown-wrapper"
                onMouseEnter={() => setIsAboutOpen(true)}
                onMouseLeave={() => setIsAboutOpen(false)}
              >
                <div className={`nav-dropdown-trigger ${isAboutOpen ? 'active' : ''}`}>
                  About
                  <svg className={`nav-arrow ${isAboutOpen ? 'rotated' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {isAboutOpen && (
                  <div className="nav-dropdown-menu">
                    <Link to="/about" className="dropdown-link">About Us</Link>
                    <Link to="/affiliate" className="dropdown-link">Become an Affiliate</Link>
                    <Link to="/careers" className="dropdown-link">Careers</Link>
                    <Link to="/evangelists" className="dropdown-link">Evangelists</Link>
                    <Link to="/events" className="dropdown-link">Events</Link>
                  </div>
                )}
              </div>
            </nav>
          )}
        </div>

        {/* Right Section: Actions */}
        <div className="header-actions">
          {/* DESKTOP ONLY: Search Icon, Login, Signup */}
          {!isMobile && (
            <>
              {/* ✅ Search Icon FIXED - display: flex explicitly */}
              <Link to="/search" className="search-icon" style={{ display: 'flex' }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </Link>

              <Link to="/login" className="login-link">
                Log in
              </Link>

              <Link to="/signup" className="signup-button">
                Sign up for free
              </Link>
            </>
          )}

          {/* MOBILE ONLY: Signup Button + Hamburger Menu */}
          {isMobile && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              {/* Mobile Signup Button */}
              <Link 
                to="/signup" 
                style={{
                  background: '#2563eb',
                  color: 'white',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '9999px',
                  fontWeight: '700',
                  fontSize: '0.75rem',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                Sign up for free
              </Link>

              {/* Mobile Hamburger Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  width: '30px',
                  height: '24px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0'
                }}
              >
                <span style={{
                  width: '100%',
                  height: '3px',
                  backgroundColor: '#374151',
                  borderRadius: '2px',
                  transform: isMobileMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none',
                  transition: 'all 0.3s'
                }}></span>
                <span style={{
                  width: '100%',
                  height: '3px',
                  backgroundColor: '#374151',
                  borderRadius: '2px',
                  opacity: isMobileMenuOpen ? 0 : 1,
                  transition: 'all 0.3s'
                }}></span>
                <span style={{
                  width: '100%',
                  height: '3px',
                  backgroundColor: '#374151',
                  borderRadius: '2px',
                  transform: isMobileMenuOpen ? 'rotate(-45deg) translate(7px, -7px)' : 'none',
                  transition: 'all 0.3s'
                }}></span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MOBILE MENU */}
      {isMobile && (
        <>
          {/* Overlay */}
          {isMobileMenuOpen && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Menu Panel */}
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '85%',
            maxWidth: '350px',
            backgroundColor: 'white',
            zIndex: 1000,
            transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s ease-out',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.25rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  width: '2rem',
                  height: '2rem',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  color: 'white',
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.5rem'
                }}>V</span>
                <span style={{
                  color: '#111827',
                  fontWeight: 700,
                  fontSize: '1.25rem'
                }}>{WEBSITE_NAME}</span>
              </div>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.8rem',
                  color: '#374151',
                  cursor: 'pointer',
                  width: '2.5rem',
                  height: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Menu Items */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: '1rem 0'
            }}>
              <Link 
                to="/get-started" 
                style={{
                  display: 'block',
                  padding: '1rem 1.5rem',
                  color: '#374151',
                  fontWeight: '500',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get Started
              </Link>
              
              <Link 
                to="/solutions" 
                style={{
                  display: 'block',
                  padding: '1rem 1.5rem',
                  color: '#374151',
                  fontWeight: '500',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Solutions
              </Link>
              
              <Link 
                to="/#pricing" 
                style={{
                  display: 'block',
                  padding: '1rem 1.5rem',
                  color: '#374151',
                  fontWeight: '500',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              
              <Link 
                to="/resources" 
                style={{
                  display: 'block',
                  padding: '1rem 1.5rem',
                  color: '#374151',
                  fontWeight: '500',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Resources
              </Link>
              
              <Link 
                to="/blog" 
                style={{
                  display: 'block',
                  padding: '1rem 1.5rem',
                  color: '#374151',
                  fontWeight: '500',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>
              
              <Link 
                to="/about" 
                style={{
                  display: 'block',
                  padding: '1rem 1.5rem',
                  color: '#374151',
                  fontWeight: '500',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>
              
              {/* Search - ROUND BOX */}
              <div style={{
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #f3f4f6'
              }}>
                <Link 
                  to="/search" 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    color: '#374151',
                    fontWeight: '500',
                    textDecoration: 'none'
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg style={{ 
                      width: '1.25rem', 
                      height: '1.25rem',
                      color: '#6b7280'
                    }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <span>Search</span>
                </Link>
              </div>
            </div>

            {/* Footer - Login */}
            <div style={{
              padding: '1.5rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <Link 
                to="/login" 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  color: '#2563eb',
                  fontWeight: '600',
                  textDecoration: 'none',
                  border: '2px solid #2563eb',
                  borderRadius: '50px'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg style={{ 
                    width: '1rem', 
                    height: '1rem',
                    color: 'white'
                  }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span>Log in</span>
              </Link>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;